import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { API_BASE_URL } from '../../config/api';
import './AdminSupportPage.css';

const STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved', 'Closed'];
const STATUS_COLORS = {
  'Open': '#ef4444',
  'In Progress': '#f59e0b',
  'Resolved': '#22c55e',
  'Closed': '#64748b'
};

const AdminSupportPage = () => {
  const { user } = useAuth();
  const { socket, on, off } = useSocket();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, open: 0, inProgress: 0, resolved: 0, closed: 0 });

  // Selected conversation
  const [selectedTicket, setSelectedTicket] = useState(null);
  const selectedTicketRef = useRef(null);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Info panel
  const [showInfoPanel, setShowInfoPanel] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Keep ref in sync
  useEffect(() => { selectedTicketRef.current = selectedTicket; }, [selectedTicket]);

  // WebSocket: join support admin room
  useEffect(() => {
    if (!socket) return;
    socket.emit('join_support_admin');
  }, [socket]);

  // WebSocket listeners
  useEffect(() => {
    if (!on || !off) return;

    const handleNewTicket = () => { fetchTickets(true); };
    const handleTicketReply = ({ ticketId }) => {
      fetchTickets(true);
      if (selectedTicketRef.current && selectedTicketRef.current.id === ticketId) {
        loadTicket(ticketId);
      }
    };
    const handleTicketUpdated = (ticket) => {
      fetchTickets(true);
      if (selectedTicketRef.current && selectedTicketRef.current.id === ticket.id) {
        // Reload full ticket to ensure replies + latest status are current
        loadTicket(ticket.id);
      }
    };

    on('new_ticket', handleNewTicket);
    on('ticket_reply', handleTicketReply);
    on('ticket_updated', handleTicketUpdated);

    return () => {
      off('new_ticket', handleNewTicket);
      off('ticket_reply', handleTicketReply);
      off('ticket_updated', handleTicketUpdated);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, off]);

  // Join/leave ticket room when selecting
  useEffect(() => {
    if (!socket || !selectedTicket) return;
    socket.emit('join_ticket', selectedTicket.id);
    return () => { socket.emit('leave_ticket', selectedTicket.id); };
  }, [socket, selectedTicket?.id]);

  const fetchTickets = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      if (!silent) setFetchError(null);
      const params = new URLSearchParams({ limit: '200' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchQuery) params.set('search', searchQuery);

      const res = await fetch(`${API_BASE_URL}/support/tickets?${params}`, { headers: authHeaders() });
      if (!res.ok) {
        const errText = await res.text();
        console.error('Admin tickets fetch failed:', res.status, errText);
        setFetchError(`Failed to load tickets (${res.status})`);
        return;
      }
      const data = await res.json();
      if (data.success) {
        setTickets(data.tickets);
        if (data.stats) setStats(data.stats);
        setFetchError(null);
      } else {
        console.error('Tickets response error:', data.error);
        setFetchError(data.error || 'Failed to load tickets');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      if (!silent) setFetchError('Network error - check connection');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Fallback polling every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchTickets(true), 5000);
    return () => clearInterval(interval);
  }, [fetchTickets]);

  // Auto scroll chat
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket]);

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-CA');
  };

  const formatMsgTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const loadTicket = async (ticketId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets/${ticketId}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setSelectedTicket(data.ticket);
    } catch (error) {
      console.error('Error fetching ticket:', error);
    }
  };

  const handleSelectTicket = (ticketId) => { loadTicket(ticketId); };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length) alert(`${oversized.map(f => `"${f.name}"`).join(', ')} exceed${oversized.length === 1 ? 's' : ''} the 10MB limit.`);
    setAttachments(prev => [...prev, ...files.filter(f => f.size <= 10 * 1024 * 1024)]);
    e.target.value = '';
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSendChat = async () => {
    if (!chatMessage.trim() && attachments.length === 0) return;
    if (!selectedTicket) return;
    setSendingChat(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('senderId', user?.id || '');
      formData.append('senderName', user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : 'Admin');
      formData.append('senderEmail', user?.email || 'admin@conflux.com');
      formData.append('senderRole', 'admin');
      formData.append('message', chatMessage.trim());
      attachments.forEach(file => formData.append('files', file));

      const res = await fetch(`${API_BASE_URL}/support/tickets/${selectedTicket.id}/replies`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setChatMessage('');
        setAttachments([]);
        loadTicket(selectedTicket.id);
        fetchTickets(true);
      } else {
        alert(`Failed to send: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingChat(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets/${ticketId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus, adminId: user?.id })
      });
      const data = await res.json();
      if (data.success) {
        if (selectedTicket && selectedTicket.id === ticketId) {
          setSelectedTicket(prev => prev ? { ...prev, status: newStatus } : prev);
        }
        fetchTickets(true);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteTicket = async (e, ticketId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this ticket permanently?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/support/tickets/${ticketId}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        if (selectedTicket && selectedTicket.id === ticketId) setSelectedTicket(null);
        fetchTickets(true);
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
    }
  };

  const parseAttachments = (raw) => {
    if (!raw) return [];
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return []; }
  };

  const renderAttachments = (raw) => {
    const files = parseAttachments(raw);
    if (!files.length) return null;
    return (
      <div className="sup-attachments">
        {files.map((file, i) => {
          const src = file.url
            ? (file.url.startsWith('http') ? file.url : `${API_BASE_URL.replace('/api', '')}${file.url}`)
            : file.data;
          const isImage = file.type && file.type.startsWith('image/');
          return (
            <div key={i} className="sup-attachment-item">
              {isImage ? (
                <a href={src} target="_blank" rel="noreferrer" download={file.name} className="sup-attachment-img-link">
                  <img src={src} alt={file.name} className="sup-attachment-img" />
                  <span className="sup-attachment-name">{file.name}</span>
                </a>
              ) : (
                <a href={src} target="_blank" rel="noreferrer" download={file.name} className="sup-attachment-file">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.5"/></svg>
                  <span>{file.name}</span>
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const getMessages = () => {
    if (!selectedTicket) return [];
    const msgs = [];
    msgs.push({
      id: 'original',
      senderId: selectedTicket.userId,
      senderRole: 'user',
      senderName: selectedTicket.userName,
      message: selectedTicket.message,
      attachments: null,
      createdAt: selectedTicket.createdAt
    });
    if (selectedTicket.adminReply && (!selectedTicket.replies || selectedTicket.replies.length === 0)) {
      msgs.push({
        id: 'legacy-admin',
        senderId: selectedTicket.adminId,
        senderRole: 'admin',
        senderName: 'Admin',
        message: selectedTicket.adminReply,
        attachments: null,
        createdAt: selectedTicket.repliedAt
      });
    }
    if (selectedTicket.replies) {
      selectedTicket.replies.forEach(r => msgs.push(r));
    }
    return msgs;
  };

  const messages = getMessages();

  const getUserType = (ticket) => {
    if (ticket.user?.isSupplier) return 'Supplier';
    return ticket.category || 'User';
  };

  return (
    <AdminLayout>
      <div className="sup-page-wrapper">
        {/* Stats Bar */}
        <div className="sup-stats-bar">
          <div className={`sup-stat-card ${statusFilter === 'all' ? 'sup-stat-active' : ''}`} onClick={() => setStatusFilter('all')}>
            <span className="sup-stat-num">{stats.total}</span>
            <span className="sup-stat-label">All Tickets</span>
          </div>
          <div className={`sup-stat-card sup-stat-open ${statusFilter === 'open' ? 'sup-stat-active' : ''}`} onClick={() => setStatusFilter('open')}>
            <span className="sup-stat-num">{stats.open}</span>
            <span className="sup-stat-label">Open</span>
          </div>
          <div className={`sup-stat-card sup-stat-progress ${statusFilter === 'in-progress' ? 'sup-stat-active' : ''}`} onClick={() => setStatusFilter('in-progress')}>
            <span className="sup-stat-num">{stats.inProgress}</span>
            <span className="sup-stat-label">In Progress</span>
          </div>
          <div className={`sup-stat-card sup-stat-resolved ${statusFilter === 'resolved' ? 'sup-stat-active' : ''}`} onClick={() => setStatusFilter('resolved')}>
            <span className="sup-stat-num">{stats.resolved}</span>
            <span className="sup-stat-label">Resolved</span>
          </div>
          <div className={`sup-stat-card sup-stat-closed ${statusFilter === 'closed' ? 'sup-stat-active' : ''}`} onClick={() => setStatusFilter('closed')}>
            <span className="sup-stat-num">{stats.closed}</span>
            <span className="sup-stat-label">Closed</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="sup-messenger">
          {/* LEFT: INBOX PANEL */}
          <div className="sup-inbox-panel">
            <div className="sup-inbox-header">
              <h2 className="sup-inbox-title">Inbox</h2>
              <div className="sup-inbox-search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="#94A3B8" strokeWidth="2"/><path d="M21 21l-4.35-4.35" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round"/></svg>
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="sup-inbox-search-input"
                />
              </div>
            </div>

            <div className="sup-inbox-list">
              {loading ? (
                <div className="sup-inbox-empty">Loading...</div>
              ) : fetchError ? (
                <div className="sup-inbox-empty">
                  <span style={{ color: '#ef4444' }}>{fetchError}</span>
                  <button onClick={() => fetchTickets()} style={{ marginTop: 8, padding: '4px 12px', background: '#1F396C', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>Retry</button>
                </div>
              ) : tickets.length === 0 ? (
                <div className="sup-inbox-empty">No tickets found</div>
              ) : (
                tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`sup-inbox-item ${selectedTicket?.id === ticket.id ? 'sup-inbox-item-active' : ''}`}
                    onClick={() => handleSelectTicket(ticket.id)}
                  >
                    <div className="sup-inbox-avatar">
                      {ticket.user?.avatar ? (
                        <img src={ticket.user.avatar} alt="" />
                      ) : (
                        <span>{ticket.userName?.charAt(0)?.toUpperCase() || 'U'}</span>
                      )}
                    </div>
                    <div className="sup-inbox-info">
                      <div className="sup-inbox-row-top">
                        <span className="sup-inbox-name">{ticket.userName}</span>
                        <span className="sup-inbox-time">{formatTimeAgo(ticket.updatedAt || ticket.createdAt)}</span>
                      </div>
                      <div className="sup-inbox-subject">
                        <span className="sup-inbox-status-dot" style={{ background: STATUS_COLORS[ticket.status] }}></span>
                        {ticket.subject}
                      </div>
                      <div className="sup-inbox-preview">
                        <span className="sup-inbox-ticket-no">{ticket.ticketNo}</span>
                        <span className="sup-inbox-cat">{ticket.category}</span>
                      </div>
                    </div>
                    <button className="sup-inbox-menu-btn" onClick={(e) => handleDeleteTicket(e, ticket.id)} title="Delete">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* CENTER: CHAT PANEL */}
          <div className="sup-chat-panel">
            {!selectedTicket ? (
              <div className="sup-chat-empty">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <path d="M52 40C52 41.41 51.44 42.77 50.44 43.77C49.44 44.77 48.08 45.33 46.67 45.33H22.67L12 56V13.33C12 11.92 12.56 10.56 13.56 9.56C14.56 8.56 15.92 8 17.33 8H46.67C48.08 8 49.44 8.56 50.44 9.56C51.44 10.56 52 11.92 52 13.33V40Z" stroke="#CBD5E1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>Select a ticket to view the conversation</p>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="sup-chat-header">
                  <div className="sup-chat-header-left">
                    <div className="sup-chat-header-avatar">
                      {selectedTicket.user?.avatar ? (
                        <img src={selectedTicket.user.avatar} alt="" />
                      ) : (
                        <span>{selectedTicket.userName?.charAt(0)?.toUpperCase() || 'U'}</span>
                      )}
                    </div>
                    <div className="sup-chat-header-info">
                      <span className="sup-chat-header-name">{selectedTicket.userName}</span>
                      <span className="sup-chat-header-meta">
                        {selectedTicket.ticketNo} &middot; {getUserType(selectedTicket)}
                      </span>
                    </div>
                  </div>
                  <div className="sup-chat-header-actions">
                    <select
                      className="sup-status-select"
                      value={selectedTicket.status}
                      onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                      style={{ borderColor: STATUS_COLORS[selectedTicket.status], color: STATUS_COLORS[selectedTicket.status] }}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <button
                      className={`sup-info-toggle ${showInfoPanel ? 'sup-info-toggle-active' : ''}`}
                      onClick={() => setShowInfoPanel(!showInfoPanel)}
                      title="Ticket details"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                    </button>
                  </div>
                </div>

                {/* Chat + Info Container */}
                <div className="sup-chat-body-wrapper">
                  {/* Messages */}
                  <div className="sup-chat-messages">
                    {messages.map((msg, idx) => {
                      const isMyMsg = msg.senderRole === 'admin' && msg.senderId == user?.id;
                      return (
                      <React.Fragment key={msg.id || idx}>
                        {idx === 0 && (
                          <div className="sup-chat-time-sep">{formatMsgTime(msg.createdAt)}</div>
                        )}
                        {idx > 0 && (() => {
                          const prev = messages[idx - 1];
                          const prevDate = prev?.createdAt ? new Date(prev.createdAt).toDateString() : '';
                          const curDate = msg.createdAt ? new Date(msg.createdAt).toDateString() : '';
                          return prevDate !== curDate ? (
                            <div className="sup-chat-time-sep">{formatMsgTime(msg.createdAt)}</div>
                          ) : null;
                        })()}
                        <div className={`sup-msg ${isMyMsg ? 'sup-msg-right' : 'sup-msg-left'}`}>
                          {!isMyMsg && (
                            <div className="sup-msg-avatar">
                              {selectedTicket.user?.avatar ? (
                                <img src={selectedTicket.user.avatar} alt="" />
                              ) : (
                                <span>{msg.senderName?.charAt(0)?.toUpperCase() || 'U'}</span>
                              )}
                            </div>
                          )}
                          <div className="sup-msg-content">
                            <div className={`sup-msg-bubble ${isMyMsg ? 'sup-msg-bubble-right' : 'sup-msg-bubble-left'}`}>
                              {msg.message}
                              {renderAttachments(msg.attachments)}
                            </div>
                            <div className={`sup-msg-time-label ${isMyMsg ? 'sup-msg-time-right' : ''}`}>
                              {isMyMsg ? 'You' : msg.senderName} &middot; {formatMsgTime(msg.createdAt)}
                            </div>
                          </div>
                          {isMyMsg && (
                            <div className="sup-msg-avatar sup-msg-avatar-admin">
                              {user?.avatar ? (
                                <img src={user.avatar} alt="" />
                              ) : (
                                <span>{user?.firstName?.charAt(0)?.toUpperCase() || 'A'}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                      );
                    })}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Info Panel */}
                  {showInfoPanel && (
                    <div className="sup-info-panel">
                      <div className="sup-info-panel-header">
                        <h3>Ticket Details</h3>
                        <button className="sup-info-close" onClick={() => setShowInfoPanel(false)}>&times;</button>
                      </div>
                      <div className="sup-info-body">
                        <div className="sup-info-section">
                          <label>Ticket No</label>
                          <span>{selectedTicket.ticketNo}</span>
                        </div>
                        <div className="sup-info-section">
                          <label>Subject</label>
                          <span>{selectedTicket.subject}</span>
                        </div>
                        <div className="sup-info-section">
                          <label>Status</label>
                          <span style={{ color: STATUS_COLORS[selectedTicket.status], fontWeight: 600 }}>
                            {selectedTicket.status}
                          </span>
                        </div>
                        <div className="sup-info-section">
                          <label>Category</label>
                          <span>{selectedTicket.category}</span>
                        </div>
                        <div className="sup-info-section">
                          <label>Priority</label>
                          <span className={`sup-info-priority sup-priority-${(selectedTicket.priority || 'medium').toLowerCase()}`}>
                            {selectedTicket.priority || 'Medium'}
                          </span>
                        </div>
                        <div className="sup-info-section">
                          <label>Created</label>
                          <span>{new Date(selectedTicket.createdAt).toLocaleString()}</span>
                        </div>
                        <div className="sup-info-section">
                          <label>Updated</label>
                          <span>{new Date(selectedTicket.updatedAt).toLocaleString()}</span>
                        </div>
                        <div className="sup-info-divider"></div>
                        <h4 className="sup-info-subtitle">User Info</h4>
                        <div className="sup-info-section">
                          <label>Name</label>
                          <span>{selectedTicket.userName}</span>
                        </div>
                        <div className="sup-info-section">
                          <label>Email</label>
                          <span>{selectedTicket.userEmail}</span>
                        </div>
                        {selectedTicket.user?.phone && (
                          <div className="sup-info-section">
                            <label>Phone</label>
                            <span>{selectedTicket.user.phone}</span>
                          </div>
                        )}
                        {selectedTicket.user?.companyName && (
                          <div className="sup-info-section">
                            <label>Company</label>
                            <span>{selectedTicket.user.companyName}</span>
                          </div>
                        )}
                        <div className="sup-info-divider"></div>
                        <h4 className="sup-info-subtitle">Change Status</h4>
                        <div className="sup-status-buttons">
                          {STATUS_OPTIONS.map(s => (
                            <button
                              key={s}
                              className={`sup-status-btn ${selectedTicket.status === s ? 'sup-status-btn-active' : ''}`}
                              style={{
                                borderColor: STATUS_COLORS[s],
                                color: selectedTicket.status === s ? '#fff' : STATUS_COLORS[s],
                                background: selectedTicket.status === s ? STATUS_COLORS[s] : 'transparent'
                              }}
                              onClick={() => handleStatusChange(selectedTicket.id, s)}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Input Bar */}
                {selectedTicket.status !== 'Closed' && selectedTicket.status !== 'Resolved' ? (
                  <div className="sup-chat-input-bar">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.txt,.zip"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    {attachments.length > 0 && (
                      <div className="sup-attach-preview-row">
                        {attachments.map((f, i) => (
                          <div key={i} className="sup-attach-chip">
                            <span className="sup-attach-chip-name">{f.name}</span>
                            <button className="sup-attach-chip-remove" onClick={() => removeAttachment(i)}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="sup-input-row">
                      <button
                        className="sup-attach-btn"
                        title="Attach file"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <input
                        type="text"
                        className="sup-chat-input"
                        placeholder="Type your reply..."
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                      />
                      <button className="sup-chat-send-btn" onClick={handleSendChat} disabled={sendingChat || (!chatMessage.trim() && attachments.length === 0)}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M18.33 1.67L9.17 10.83M18.33 1.67L12.5 18.33L9.17 10.83M18.33 1.67L1.67 7.5L9.17 10.83" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                  </div>
                ) : selectedTicket.status === 'Resolved' ? (
                  <div className="sup-chat-closed-bar sup-chat-resolved-bar">
                    Ticket is resolved. Reopen to continue conversation.
                    <button className="sup-reopen-btn" onClick={() => handleStatusChange(selectedTicket.id, 'In Progress')}>Reopen</button>
                  </div>
                ) : (
                  <div className="sup-chat-closed-bar">
                    This ticket is closed.
                    <button className="sup-reopen-btn" onClick={() => handleStatusChange(selectedTicket.id, 'Open')}>Reopen</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSupportPage;
